import { Thread, useThreadsStore } from "@store/threads.store";
import { useEditThreadTitle } from "@queries/chat/useEditThreadTitle";
import { useThreadLogic } from "@features/Chat/hooks/useThreadLogic";
import { useUIStore } from "@store/ui.store";
import {
  ThreadsContainer,
  ThreadsList,
  ThreadsGroup,
  ThreadGroupTitle,
} from "./styles";
import { Typography } from "@mui/material";
import { groupThreadsForDisplay } from "../thread-helpers";
import { ThreadItem } from "./ThreadItem";
import { useCallback } from "react";
import { usePinThread } from "@queries/chat/usePinThread";

type Props = {
  currentThread: string | undefined;
  navigateToThread: (id: string | null) => void;
};

export const Threads = (props: Props) => {
  const { currentThread, navigateToThread } = props;
  const threads = useThreadsStore((state) => state.threads);
  const updateThread = useThreadsStore((state) => state.updateThread);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const messageInputText = useUIStore((state) => state.messageInputText);
  const groupedThreads = groupThreadsForDisplay(threads);

  const editThreadTitleMutation = useEditThreadTitle();
  const { deleteThread } = useThreadLogic();
  const pinOrUnpinThreadMutation = usePinThread();

  const handleClickThread = useCallback(
    (threadId: string) => {
      navigateToThread(threadId);
      toggleSidebar(false);
      messageInputText?.focus();
    },
    [navigateToThread, toggleSidebar, messageInputText],
  );

  const onRenameThread = useCallback(
    (editThreadId: string, title: string) => {
      if (!title) return;

      const thread = threads.find((thread) => thread.id === editThreadId);

      if (title === thread?.title) {
        return;
      }

      updateThread(editThreadId, { title });

      editThreadTitleMutation.mutate({
        threadId: editThreadId,
        title,
      });
    },
    [threads, updateThread, editThreadTitleMutation],
  );

  const handlePinClick = useCallback(
    (threadId: string) => {
      updateThread(threadId, { pinned: true });

      pinOrUnpinThreadMutation.mutate({
        threadId,
        pinned: true,
      });
    },
    [updateThread, pinOrUnpinThreadMutation],
  );

  const handleUnpinClick = useCallback(
    (threadId: string) => {
      updateThread(threadId, { pinned: false });

      pinOrUnpinThreadMutation.mutate({
        threadId,
        pinned: false,
      });
    },
    [updateThread, pinOrUnpinThreadMutation],
  );

  const handleDeleteClick = useCallback(
    async (threadId: string) => {
      const thread = threads.find((thread) => thread.id === threadId);
      const response = await confirmDialog({
        title: "Delete chat?",
        content: (
          <Typography>
            This will delete <strong>{thread?.title}</strong>
          </Typography>
        ),
        confirmButtonText: "Delete",
      });

      if (!response) return;

      deleteThread(threadId);
    },
    [confirmDialog, deleteThread, threads],
  );

  const renderThread = useCallback(
    (thread: Thread) => {
      return (
        <ThreadItem
          isFirstThread={thread.id === threads[0].id}
          key={thread.id}
          thread={thread}
          handleClickThread={handleClickThread}
          onRenameThread={onRenameThread}
          handlePinClick={handlePinClick}
          handleUnpinClick={handleUnpinClick}
          handleDeleteClick={handleDeleteClick}
          currentThread={currentThread}
        />
      );
    },
    [
      currentThread,
      handleClickThread,
      handleDeleteClick,
      onRenameThread,
      handlePinClick,
      handleUnpinClick,
      threads,
    ],
  );

  return (
    <ThreadsContainer>
      <ThreadsList>
        {groupedThreads.pinned.length > 0 && (
          <ThreadsGroup>
            <ThreadGroupTitle variant="h6">Pinned</ThreadGroupTitle>
            {groupedThreads.pinned.map(renderThread)}
          </ThreadsGroup>
        )}
        {groupedThreads.today.length > 0 && (
          <ThreadsGroup>
            <ThreadGroupTitle variant="h6">Today</ThreadGroupTitle>
            {groupedThreads.today.map(renderThread)}
          </ThreadsGroup>
        )}
        {groupedThreads.last7Days.length > 0 && (
          <ThreadsGroup>
            <ThreadGroupTitle variant="h6">Previous 7 days</ThreadGroupTitle>
            {groupedThreads.last7Days.map(renderThread)}
          </ThreadsGroup>
        )}
        {groupedThreads.last30Days.length > 0 && (
          <ThreadsGroup>
            <ThreadGroupTitle variant="h6">Previous 30 days</ThreadGroupTitle>
            {groupedThreads.last30Days.map(renderThread)}
          </ThreadsGroup>
        )}
      </ThreadsList>
    </ThreadsContainer>
  );
};
